import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfigDB from "../../config";

// Lista de línguas suportadas em todo o produto. Mantida em sintonia
// com PublicLanguageSwitcher.js — qualquer mudança aqui deve replicar.
const LANGUAGES = [
  { code: "pt", name: "Português", flag: "🇵🇹" },
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
];

const LanguageSwitcher = ({ profileDetails }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  // Sincroniza estado inicial com o que está em localStorage. Se nada
  // estiver guardado, default = pt.
  useEffect(() => {
    const savedLanguage = localStorage.getItem("lng") || "pt";
    if (savedLanguage !== i18n.language) i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  // Fecha o dropdown ao clicar fora.
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const current =
    LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleChangeLanguage = (lng) => {
    localStorage.setItem("lng", lng);
    if (profileDetails) {
      // Mantém o perfil em localStorage para outras partes da app não
      // perderem dados ao recarregar.
      localStorage.setItem(
        ConfigDB.data.login_data,
        JSON.stringify(profileDetails),
      );
    }
    setIsOpen(false);
    i18n.changeLanguage(lng);
    window.location.reload();
  };

  return (
    <div
      ref={ref}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{
          background: "#fff",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          padding: "6px 12px",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "14px",
          color: "#212529",
          minHeight: 36,
          transition: "border-color 120ms ease, box-shadow 120ms ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#adb5bd")}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#dee2e6")}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{current.flag}</span>
        <span style={{ fontWeight: 500 }}>{current.code.toUpperCase()}</span>
        <i
          className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
          style={{ fontSize: 12, color: "#6c757d" }}
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            margin: 0,
            padding: 4,
            listStyle: "none",
            background: "#fff",
            border: "1px solid #e9ecef",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            minWidth: 180,
            zIndex: 1050,
          }}
        >
          {LANGUAGES.map((lang) => {
            const isSelected = lang.code === current.code;
            return (
              <li key={lang.code}>
                <button
                  type="button"
                  onClick={() => handleChangeLanguage(lang.code)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    background: isSelected ? "#f1f3f5" : "transparent",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 14,
                    textAlign: "left",
                    color: "#212529",
                    fontWeight: isSelected ? 600 : 400,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>
                    {lang.flag}
                  </span>
                  <span style={{ flex: 1 }}>{lang.name}</span>
                  {isSelected && (
                    <i
                      className="bi bi-check2"
                      style={{ color: "#198754", fontSize: 16 }}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
